import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  InfoCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  FileSearchOutlined,
  RobotOutlined,
  SafetyOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';
import {
  getAgentInstructionLint,
  LINT_SEVERITY,
  type AgentInstructionLintResult,
  type LintFinding,
  type LintSeverity,
} from '@/api/instructionLint';
import { RuleDetailDrawer, ruleDetailFromFinding } from './RuleDetailDrawer';

const { Text, Title, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// InstructionLintPage — author-time quality checks for agent instructions
// ─────────────────────────────────────────────────────────────────────────────
//
// AUDIENCE
//   Clinical SMEs, agent designers, prompt engineers — the people who own
//   the body of an agent's instructions. They want to know:
//     • Does my agent's instruction body reference the skills I attached?
//     • Does it say what to do when the patient mentions something unsafe?
//     • Did I forget a citation policy block?
//     • Are there drug-specific safety rules I missed for THIS agent?
//
//   Lint catches these BEFORE the agent ships — same idea as compiler
//   warnings, but for prose. Each finding has a severity and (where the
//   rule has an auto-fix) a pre-rendered Markdown snippet ready to paste
//   into the instruction editor.
//
// LAYOUT
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ Page header — "Instruction Lint"                                 │
//   ├──────────────────────────────────────────────────────────────────┤
//   │ Top bar: Agent picker + Run button + summary chips              │
//   ├──────────────────────────────────────────────────────────────────┤
//   │ Findings (one card per fired rule):                              │
//   │   • Severity icon (Block/Warn/Info)                              │
//   │   • Rule id + category + title                                   │
//   │   • Plain-language message                                       │
//   │   • Missing references (if any) as chips                         │
//   │   • Suggested snippet (Markdown code block + Copy button)        │
//   │   • "What this rule checks" hover                                │
//   ├──────────────────────────────────────────────────────────────────┤
//   │ Empty state when no agent picked: friendly invitation            │
//   │ Empty state when agent has zero findings: "all clear" celebration│
//   └──────────────────────────────────────────────────────────────────┘
//
// VISUAL CONTRACT FOR SEVERITY (matches help-drawer copy + the lint engine)
//   Block → red,    blocks save when EnforceX is true
//   Warn  → amber,  yellow advisory; save proceeds
//   Info  → blue,   informational nudge
// ─────────────────────────────────────────────────────────────────────────────

export const InstructionLintPage = observer(function InstructionLintPage() {
  const { themeStore, merchantContextStore: ctx } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [agentId, setAgentId] = useState<number | null>(null);
  const [result, setResult] = useState<AgentInstructionLintResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Selected finding drives the rule-detail drawer. Page-level state because
  // FindingCard children open the same drawer instance — only one open at
  // a time, so a single state slot is the right shape (cf. lint-ux-evolution.md §2.1).
  const [openRuleFinding, setOpenRuleFinding] = useState<LintFinding | null>(null);

  // Auto-fetch when an agent is picked. Re-fetch when the user clicks
  // Run (via the explicit refresh button or the auto-fetch hook firing
  // for a new id). Loading state is shared between both paths.
  useEffect(() => {
    if (agentId == null) {
      setResult(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await getAgentInstructionLint(agentId);
        if (!cancelled) setResult(r);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load lint');
          setResult(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const refresh = async () => {
    if (agentId == null) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getAgentInstructionLint(agentId);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lint');
    } finally {
      setLoading(false);
    }
  };

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<FileSearchOutlined />}
          title="Pick a merchant to begin"
          description="Use the merchant switcher in the top bar to scope the agents you can lint."
          size="lg"
        />
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        accent="violet"
        icon={<FileSearchOutlined />}
        title="Instruction Lint"
        helpTopic="lint.overview"
        subtitle="Catch authoring issues in your agent's instructions before they reach a patient or clinician"
      />

      {/* ─── Picker + Run row ──────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          padding: '12px 14px',
          background: isDark ? colors.slate[800] : '#FFFFFF',
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 8,
        }}
      >
        <Text strong style={{ fontSize: 13 }}>
          Pick an agent to check:
        </Text>
        <AgentPickerTrigger
          value={agentId}
          onChange={(id) => setAgentId(id)}
          placeholder="Choose an agent"
          drawerTitle="Pick an agent to lint"
          clearLabel="(none)"
          style={{ minWidth: 280 }}
        />
        {agentId != null && (
          <Tooltip title="Re-run all rules against the agent's current instruction body">
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => void refresh()}
              aria-label="Re-run lint"
            >
              Re-run
            </Button>
          </Tooltip>
        )}
        <span style={{ flex: 1 }} />
        {result && <SummaryChips result={result} isDark={isDark} />}
      </div>

      {/* ─── Body ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert
          type="error"
          showIcon
          message="Couldn't run lint"
          description={error}
          closable
          onClose={() => setError(null)}
        />
      )}

      {loading && (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 4 }} />
        </SectionCard>
      )}

      {!loading && !error && agentId == null && (
        <SectionCard>
          <BrandedEmpty
            icon={<FileSearchOutlined />}
            title="Choose an agent to begin"
            description="Lint reads your agent's instructions and checks them against grounding, clinical-safety, and authoring-discipline rules. Pick an agent above to see what it would flag."
            size="lg"
          />
        </SectionCard>
      )}

      {!loading && !error && agentId != null && result && (
        <ResultPanel
          result={result}
          isDark={isDark}
          onOpenRuleDetail={setOpenRuleFinding}
        />
      )}

      {/* Rule-detail drawer — single instance, opened by any FindingCard's
          rule-id chip click. Closed when openRuleFinding === null. */}
      <RuleDetailDrawer
        detail={openRuleFinding ? ruleDetailFromFinding(openRuleFinding) : null}
        onClose={() => setOpenRuleFinding(null)}
        isDark={isDark}
      />
    </div>
  );
});

// ─── Summary chips at the top right of the picker row ──────────────────────

function SummaryChips({
  result,
  isDark,
}: {
  result: AgentInstructionLintResult;
  isDark: boolean;
}) {
  const chip = (label: string, count: number, tone: 'red' | 'amber' | 'green' | 'slate') => {
    const palette: Record<string, { fg: string; bg: string }> = {
      red: { fg: colors.red[700], bg: isDark ? `${colors.red[700]}22` : colors.red[100] },
      amber: { fg: colors.amber[700], bg: isDark ? `${colors.amber[700]}22` : colors.amber[100] },
      green: { fg: colors.green[700], bg: isDark ? `${colors.green[700]}22` : colors.green[100] },
      slate: { fg: colors.slate[600], bg: isDark ? `${colors.slate[700]}22` : colors.slate[100] },
    };
    const p = palette[tone];
    return (
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11.5,
          padding: '3px 10px',
          borderRadius: 999,
          background: p.bg,
          color: p.fg,
          fontWeight: 600,
        }}
      >
        {count} {label}
      </span>
    );
  };

  return (
    <Space size={6}>
      {chip('blocking', result.blockingFindings, result.blockingFindings > 0 ? 'red' : 'slate')}
      {chip('advisory', result.advisoryFindings, result.advisoryFindings > 0 ? 'amber' : 'slate')}
      {chip(
        result.findings.length === 0 ? 'all rules pass' : 'rules fired',
        result.findings.length === 0 ? result.rulesEvaluated : result.rulesFired,
        result.findings.length === 0 ? 'green' : 'slate',
      )}
    </Space>
  );
}

// ─── Main result panel ────────────────────────────────────────────────────

function ResultPanel({
  result,
  isDark,
  onOpenRuleDetail,
}: {
  result: AgentInstructionLintResult;
  isDark: boolean;
  onOpenRuleDetail: (finding: LintFinding) => void;
}) {
  // "All clear" celebration when zero findings — clinical users deserve
  // a positive signal as much as an actionable one.
  if (result.findings.length === 0) {
    return (
      <SectionCard>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '40px 20px',
            textAlign: 'center',
          }}
        >
          <CheckCircleFilled style={{ fontSize: 56, color: colors.green[500] }} aria-hidden />
          <Title level={4} style={{ margin: 0 }}>
            All clear
          </Title>
          <Text style={{ color: isDark ? colors.slate[300] : colors.slate[600] }}>
            No issues found in <strong>{result.agentDisplayName}</strong>'s instructions.
            All {result.rulesEvaluated} rules passed.
          </Text>
          <AgentSummaryLine result={result} isDark={isDark} />
        </div>
      </SectionCard>
    );
  }

  // Otherwise: a card per finding, severity-grouped (Block first, then
  // Warn, then Info — same priority order an operator would tackle them).
  const sorted = [...result.findings].sort(
    (a, b) => severitySortKey(b.severity) - severitySortKey(a.severity),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionCard>
        <AgentSummaryLine result={result} isDark={isDark} />
      </SectionCard>

      {result.hasBlockers && (
        <Alert
          type="error"
          showIcon
          message="One or more rules are blocking"
          description={
            <span>
              This agent declared <Text code>EnforceKbLoad</Text> or{' '}
              <Text code>EnforceSkillLoad</Text>, and at least one rule of that category fired.
              Save will be blocked in the agent editor until these are resolved.
            </span>
          }
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sorted.map((f) => (
          <FindingCard
            key={`${f.ruleId}-${f.title}`}
            finding={f}
            isDark={isDark}
            onOpenRuleDetail={onOpenRuleDetail}
          />
        ))}
      </div>
    </div>
  );
}

// One-liner that summarises the agent under inspection. Lives at the top
// of the result panel + at the bottom of the "all clear" celebration.
function AgentSummaryLine({
  result,
  isDark,
}: {
  result: AgentInstructionLintResult;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
        color: isDark ? colors.slate[300] : colors.slate[600],
      }}
    >
      <RobotOutlined aria-hidden />
      <strong style={{ color: isDark ? colors.slate[100] : colors.slate[900] }}>
        {result.agentDisplayName}
      </strong>
      {result.canonicalName && (
        <Text type="secondary" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
          {result.canonicalName}
        </Text>
      )}
      {result.audience && <Tag>{result.audience}</Tag>}
      <span style={{ color: isDark ? colors.slate[500] : colors.slate[400] }}>·</span>
      <span>
        <strong>{result.linkedSkillCount}</strong> skills
      </span>
      <span>
        <strong>{result.linkedKnowledgeCount}</strong> knowledge bases
      </span>
      <span>
        <strong>{result.enabledActionCount}</strong> tools
      </span>
      {result.enforceKbLoad && (
        <Tag color="red" icon={<SafetyOutlined />}>
          Enforce KB load
        </Tag>
      )}
      {result.enforceSkillLoad && (
        <Tag color="red" icon={<SafetyOutlined />}>
          Enforce skill load
        </Tag>
      )}
    </div>
  );
}

// ─── Per-finding card ─────────────────────────────────────────────────────

function FindingCard({
  finding,
  isDark,
  onOpenRuleDetail,
}: {
  finding: LintFinding;
  isDark: boolean;
  onOpenRuleDetail: (finding: LintFinding) => void;
}) {
  const tone = severityTone(finding.severity);
  const SeverityIcon =
    finding.severity === LINT_SEVERITY.Block
      ? CloseCircleFilled
      : finding.severity === LINT_SEVERITY.Warn
        ? WarningFilled
        : InfoCircleOutlined;

  // Rule-id chip is the primary affordance for opening the rule-detail
  // drawer (lint-ux-evolution.md §2.1). Click + Enter both work; the chip
  // looks like a clickable element via cursor/border-on-hover styling
  // pushed inline.
  const openDetail = () => onOpenRuleDetail(finding);
  const onChipKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${tone.fg}33`,
        borderLeft: `4px solid ${tone.fg}`,
        borderRadius: 8,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {/* Header: icon + rule id + category + title */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <SeverityIcon
          style={{ fontSize: 22, color: tone.fg, flexShrink: 0, marginTop: 2 }}
          aria-hidden
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <Text
              strong
              style={{
                fontSize: 14,
                color: isDark ? colors.slate[50] : colors.slate[900],
              }}
            >
              {finding.title}
            </Text>
            <Tooltip
              title={
                finding.whatItChecks
                  ? `${finding.whatItChecks} — click for the full rule details`
                  : 'Click for full rule details'
              }
              mouseEnterDelay={0.1}
            >
              <span
                role="button"
                tabIndex={0}
                onClick={openDetail}
                onKeyDown={onChipKeyDown}
                aria-label={`Open rule details for ${finding.ruleId}`}
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  padding: '2px 7px',
                  borderRadius: 4,
                  background: isDark ? colors.slate[700] : colors.slate[100],
                  color: isDark ? colors.slate[300] : colors.slate[600],
                  cursor: 'pointer',
                  border: `1px solid transparent`,
                  transition: 'border-color 120ms ease, background 120ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = isDark
                    ? colors.slate[500]
                    : colors.slate[400];
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = colors.violet[600];
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                {finding.ruleId}
              </span>
            </Tooltip>
            <CategoryBadge
              category={finding.category}
              description={finding.categoryDescription}
              isDark={isDark}
            />
            <SeverityLabel
              severity={finding.severity}
              description={finding.severityDescriptor?.shortDescription ?? null}
            />
          </div>
          <Paragraph
            style={{
              margin: '6px 0 0 0',
              fontSize: 13,
              color: isDark ? colors.slate[200] : colors.slate[700],
              lineHeight: 1.5,
            }}
          >
            {finding.message}
          </Paragraph>
        </div>
      </div>

      {/* Missing references chips — when the rule called out specific
          skills/KB items not mentioned in the instruction body */}
      {finding.missingReferences && finding.missingReferences.length > 0 && (
        <div
          style={{
            paddingLeft: 34,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Text
            type="secondary"
            style={{ fontSize: 12, fontWeight: 500 }}
          >
            Not referenced:
          </Text>
          {finding.missingReferences.map((r) => (
            <Tag
              key={r}
              style={{
                background: isDark ? colors.slate[700] : colors.slate[100],
                color: isDark ? colors.slate[200] : colors.slate[700],
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                borderColor: 'transparent',
              }}
            >
              {r}
            </Tag>
          ))}
        </div>
      )}

      {/* Suggested snippet — pre-rendered Markdown ready to paste */}
      {finding.suggestedSnippetMarkdown && (
        <SnippetCard
          markdown={finding.suggestedSnippetMarkdown}
          isDark={isDark}
        />
      )}
    </div>
  );
}

function SnippetCard({ markdown, isDark }: { markdown: string; isDark: boolean }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard?.writeText(markdown);
      message.success('Snippet copied — paste it into the instruction editor');
    } catch {
      message.error('Copy failed');
    }
  };

  return (
    <div
      style={{
        marginLeft: 34,
        background: isDark ? colors.slate[900] : colors.slate[50],
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 12px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <Text
          strong
          style={{
            fontSize: 11,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          Suggested fix — paste into your instructions
        </Text>
        <Tooltip title="Copy snippet to clipboard">
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={onCopy}
            aria-label="Copy snippet"
          >
            Copy
          </Button>
        </Tooltip>
      </div>
      <pre
        style={{
          margin: 0,
          padding: 12,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12.5,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: isDark ? colors.slate[200] : colors.slate[800],
          background: 'transparent',
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {markdown}
      </pre>
    </div>
  );
}

// ─── Severity helpers ─────────────────────────────────────────────────────

function severitySortKey(s: LintSeverity): number {
  return s === LINT_SEVERITY.Block ? 3 : s === LINT_SEVERITY.Warn ? 2 : 1;
}

function severityTone(s: LintSeverity): { fg: string; label: string } {
  if (s === LINT_SEVERITY.Block) return { fg: colors.red[600], label: 'Blocking' };
  if (s === LINT_SEVERITY.Warn) return { fg: colors.amber[600], label: 'Advisory' };
  return { fg: colors.ink[500], label: 'Info' };
}

function SeverityLabel({
  severity,
  description,
}: {
  severity: LintSeverity;
  description: string | null;
}) {
  const tone = severityTone(severity);
  // Hover popover on severity chip — content from LintSeverityCatalog
  // (lint-ux-evolution.md §2.6). Falls back to a generic message when the
  // backend hasn't populated the descriptor (older API responses).
  const tip =
    description ??
    (severity === LINT_SEVERITY.Block
      ? 'Save is disabled until resolved (only when EnforceX is on for this category).'
      : severity === LINT_SEVERITY.Warn
        ? "Won't break anything immediately. Reduces trust over time."
        : 'Telemetry only — a nudge, not a gate.');
  return (
    <Tooltip title={tip} mouseEnterDelay={0.1}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          padding: '2px 7px',
          borderRadius: 4,
          background: `${tone.fg}1A`,
          color: tone.fg,
          cursor: 'help',
        }}
      >
        {tone.label}
      </span>
    </Tooltip>
  );
}

function CategoryBadge({
  category,
  description,
  isDark,
}: {
  category: string;
  description: string | null;
  isDark: boolean;
}) {
  // Map known categories to icons + colors. Falls through to a neutral
  // tag for forward-compatibility with future rule categories.
  const palette: Record<string, { icon: React.ReactNode; fg: string; bg: string }> = {
    Grounding: {
      icon: <ExperimentOutlined aria-hidden />,
      fg: colors.violet[700],
      bg: isDark ? `${colors.violet[700]}22` : colors.violet[100],
    },
    'Clinical safety': {
      icon: <SafetyOutlined aria-hidden />,
      fg: colors.red[700],
      bg: isDark ? `${colors.red[700]}22` : colors.red[100],
    },
    'Anti-patterns': {
      icon: <WarningFilled aria-hidden />,
      fg: colors.amber[700],
      bg: isDark ? `${colors.amber[700]}22` : colors.amber[100],
    },
  };
  const p =
    palette[category] ?? {
      icon: null,
      fg: isDark ? colors.slate[300] : colors.slate[600],
      bg: isDark ? colors.slate[700] : colors.slate[100],
    };

  // Hover popover on category chip — content from LintCategoryCatalog
  // (lint-ux-evolution.md §2.6). Falls back to a generic message when the
  // backend hasn't populated a description for this category yet.
  const tip =
    description ??
    `Authoring rules grouped under "${category}". Click any rule code on this page for the full rationale.`;

  return (
    <Tooltip title={tip} mouseEnterDelay={0.1}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          fontWeight: 500,
          padding: '2px 8px',
          borderRadius: 999,
          background: p.bg,
          color: p.fg,
          cursor: 'help',
        }}
      >
        {p.icon}
        {category}
      </span>
    </Tooltip>
  );
}
